import { Box, CircularProgress } from "@mui/material";
import { useAtom } from "jotai";
import { type FC, type PropsWithChildren, useEffect } from "react";
import { createContext, useContextSelector } from "use-context-selector";
import { getRightsV2 } from "web_myplann/Infrastructure/Api/Api";
import {
	processResponse,
	withTimeout,
} from "web_myplann/Infrastructure/Api/ApiUtils";
import { usePilotPageBackground } from "web_myplann/Infrastructure/Hooks/usePilotPageBackground";
import { rightsState } from "web_myplann/Shared/State/rightsAtom";
import {
	NO_PILOT_RIGHTS,
	type PilotRights,
} from "web_myplann/Utils/WebViewModel";

type RightsContextValue = {
	data: PilotRights | undefined;
	isPending: boolean;
};

const RightsContext = createContext<RightsContextValue | undefined>(undefined);

const FALLBACK_FETCH_TIMEOUT_MS = 10_000;

const fetchRightsWithTimeout = async (): Promise<PilotRights> => {
	try {
		return await processResponse(
			withTimeout(
				(_: undefined, options) => getRightsV2(options),
				FALLBACK_FETCH_TIMEOUT_MS,
			),
		)(undefined);
	} catch {
		return NO_PILOT_RIGHTS;
	}
};

export const RightsProvider: FC<PropsWithChildren> = ({ children }) => {
	const [rights, setRights] = useAtom(rightsState);

	// BACKWARD COMPAT (DEV-3888): the fallback fetch and the loader below only
	// serve native shells that do not send `rights` in INIT_RESPONSE, and the
	// browser/E2E run. See useInitializeWeb for when to remove it.
	useEffect(() => {
		if (rights) {
			return;
		}
		let cancelled = false;
		fetchRightsWithTimeout().then((resolved) => {
			if (!cancelled) {
				setRights(resolved);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [rights, setRights]);

	usePilotPageBackground(rights?.pilotGoal);

	if (!rights) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<RightsContext.Provider value={{ data: rights, isPending: false }}>
			{children}
		</RightsContext.Provider>
	);
};

export const useRights = <T,>(selector: (rights: PilotRights) => T): T =>
	useContextSelector(RightsContext, (context) => {
		if (!context?.data) {
			throw new Error("useRights must be used within RightsProvider");
		}
		return selector(context.data);
	});

export const useGoalsRight = () => useRights((rights) => rights.pilotGoal);

export const useGoalsRightOptional = () =>
	useContextSelector(
		RightsContext,
		(context) => context?.data?.pilotGoal ?? false,
	);

export const usePortfolioRightOptional = () => {
	const hasRight = useContextSelector(
		RightsContext,
		(context) => context?.data?.pilotMyplannPortfolio ?? false,
	);
	const isPending = useContextSelector(
		RightsContext,
		(context) => context?.isPending ?? false,
	);
	return { hasRight, isPending };
};

export const useNotificationCenterRightOptional = () =>
	useContextSelector(
		RightsContext,
		(context) => context?.data?.pilotNotificationCenter ?? false,
	);

export const useRemindersRightOptional = () =>
	useContextSelector(
		RightsContext,
		(context) => context?.data?.pilotMyplannReminders ?? false,
	);
